import React, { useEffect, useState } from 'react';

export interface ToastProps {
  message: string;
  type: 'success' | 'error' | 'warning' | 'info';
  duration?: number;
  onClose: () => void;
}

const Toast: React.FC<ToastProps> = ({ message, type, duration = 5000, onClose }) => {
  const [isVisible, setIsVisible] = useState(true);

  useEffect(() => {
    const timer = setTimeout(() => {
      setIsVisible(false);
      setTimeout(onClose, 300); // Wait for animation to complete
    }, duration);

    return () => clearTimeout(timer);
  }, [duration, onClose]);

  const handleClose = () => {
    setIsVisible(false);
    // Wait for animation to complete before removing from DOM
    setTimeout(onClose, 300);
  };

  const getToastClass = () => {
    const baseClass = 'toast';
    const typeClass = `toast-${type}`;
    const visibilityClass = isVisible ? 'toast-visible' : 'toast-hidden';
    return `${baseClass} ${typeClass} ${visibilityClass}`;
  };

  const getIconSvg = () => {
    // Use currentColor so the container can define color per type via CSS
    const symbol = (() => {
      switch (type) {
        case 'success':
          return (<path d="m15.25 19 2.5 2.5 5-5m4.583 2.5a8.333 8.333 0 1 1-16.667 0 8.333 8.333 0 0 1 16.667 0z" stroke="currentColor" strokeWidth="1.667" strokeLinecap="round" strokeLinejoin="round"/>);
        case 'error':
          return (
            <g>
              <path d="M27 19a8 8 0 1 1-16 0 8 8 0 0 1 16 0z" stroke="currentColor" strokeWidth="1.667"/>
              <path d="M15.5 15.5 22.5 22.5M22.5 15.5 15.5 22.5" stroke="currentColor" strokeWidth="1.667" strokeLinecap="round"/>
            </g>
          );
        case 'warning':
          return (
            <g>
              <path d="M27 19a8 8 0 1 1-16 0 8 8 0 0 1 16 0z" stroke="currentColor" strokeWidth="1.667"/>
              <path d="M19 15v5" stroke="currentColor" strokeWidth="1.667" strokeLinecap="round"/>
              <circle cx="19" cy="22.5" r="1" fill="currentColor"/>
            </g>
          );
        default:
          return (
            <g>
              <path d="M27 19a8 8 0 1 1-16 0 8 8 0 0 1 16 0z" stroke="currentColor" strokeWidth="1.667"/>
              <path d="M19 16v4" stroke="currentColor" strokeWidth="1.667" strokeLinecap="round"/>
              <circle cx="19" cy="22.5" r="1" fill="currentColor"/>
            </g>
          );
      }
    })();

    return (
      <svg width="38" height="38" viewBox="0 0 38 38" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden>
        <path d="M19 6c7.18 0 13 5.82 13 13s-5.82 13-13 13S6 26.18 6 19 11.82 6 19 6z" stroke="currentColor" strokeWidth="2" opacity=".3"/>
        <path d="M19 1c9.941 0 18 8.059 18 18s-8.059 18-18 18S1 28.941 1 19 9.059 1 19 1z" stroke="currentColor" strokeWidth="2" opacity=".1"/>
        <g clipPath="url(#odoixxy5ja)">{symbol}</g>
        <defs>
          <clipPath id="odoixxy5ja">
            <path fill="#fff" transform="translate(9 9)" d="M0 0h20v20H0z"/>
          </clipPath>
        </defs>
      </svg>
    );
  };

  return (
    <div className={getToastClass()} role="status" aria-live="polite">
      <div className="toast-content">
        <span className="toast-icon-svg">{getIconSvg()}</span>
        <div className="toast-text">
          <div className="toast-title">
            {type === 'success' ? 'Successfully updated' : type === 'error' ? 'Something went wrong' : type === 'warning' ? 'Warning' : 'Notice'}
          </div>
          <div className="toast-message">{message}</div>
        </div>
        <button className="toast-close" onClick={handleClose} aria-label="Close notification">
          ×
        </button>
      </div>
    </div>
  );
};

export default Toast; 